import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { API_URL } from "../../config/apiConfig";
import {
  FaFilter,
  FaDownload,
  FaEye,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaRupeeSign,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";

const RecentTransactionsTable = ({
  transactions,
  isLoading,
  onLoadMore,
  hasMore,
  defaultFilter = "all",
}) => {
  const [filter, setFilter] = useState(defaultFilter);
  const auth = useSelector((state) => state.auth);
  const token = auth?.user?.token;
  const [bookingInfoMap, setBookingInfoMap] = useState({});

  // Fetch booking details to fill missing driver info when necessary
  useEffect(() => {
    if (!Array.isArray(transactions) || transactions.length === 0) return;

    const missingIds = Array.from(
      new Set(
        transactions
          .filter((t) => !t?.driverName)
          .map((t) => {
            const bId = t?.transaction?.bookingId;
            if (typeof bId === 'object' && bId !== null) {
              return bId._id || bId.bookingId;
            }
            return bId || t?.transaction?.referenceId || t?.transaction?.bookingNumber;
          })
          .filter(Boolean)
      )
    ).filter((id) => !bookingInfoMap[id]);

    if (missingIds.length === 0) return;

    const source = axios.CancelToken.source();
    const config = token ? { headers: { Authorization: `Bearer ${token}` }, cancelToken: source.token } : { cancelToken: source.token };

    Promise.allSettled(
      missingIds.map(async (id) => {
        try {
          const res = await axios.get(`${API_URL}/booking/${id}`, config);
          const data = res?.data?.data;
          if (!data) return null;

          // Determine driver (admin or vendor)
          const driver = data.driver || data.vendorDriver || null;
          const driverName = driver?.name || (driver?.firstName || driver?.lastName ? `${driver.firstName || ""} ${driver.lastName || ""}`.trim() : null);
          const driverVehicle = driver?.vehicleNumber || driver?.vehicleModel || driver?.vehicleDetails?.registrationNumber || null;

          return { id, driverName, driverVehicle };
        } catch (e) {
          return null;
        }
      })
    ).then((results) => {
      const updates = {};
      results.forEach((r) => {
        if (r.status === "fulfilled" && r.value && (r.value.driverName || r.value.driverVehicle)) {
          updates[r.value.id] = { driverName: r.value.driverName, driverVehicle: r.value.driverVehicle };
        }
      });
      if (Object.keys(updates).length > 0) {
        setBookingInfoMap((prev) => ({ ...prev, ...updates }));
      }
    });

    return () => source.cancel("component unmounted");
  }, [transactions, token]);

  const getTransactionIcon = (type) => {
    switch (type) {
      case "ride_earning":
      case "online_ride_earning":
      case "ride_payment_received":
        return <FaArrowUp className="text-green-500" />;
      case "commission_deduction":
      case "admin_commission_deducted":
      case "vendor_commission_credited":
      case "commission_payout":
      case "cod_commission_deduction":
        return <FaArrowDown className="text-red-500" />;
      case "withdrawal_request":
        return <FaClock className="text-yellow-500" />;
      case "withdrawal_approved":
        return <FaCheckCircle className="text-green-500" />;
      case "commission_payment":
      case "admin_commission_credited":
        return <FaRupeeSign className="text-blue-500" />;
      default:
        return <FaClock className="text-gray-500" />;
    }
  };

  const getTransactionTypeDisplay = (type) => {
    switch (type) {
      case "ride_earning":
      case "online_ride_earning":
        return "Ride Earning";
      case "ride_payment_received":
        return "Ride Payment Received";
      case "commission_deduction":
      case "cod_commission_deduction":
        return "Commission";
      case "admin_commission_credited":
        return "Admin Commission Credited";
      case "vendor_commission_credited":
        return "Vendor Commission Credited";
      case "admin_commission_deducted":
        return "Admin Commission Deducted";
      case "commission_payout":
        return "Vendor Payout";
      case "withdrawal_request":
        return "Withdrawal";
      case "withdrawal_approved":
        return "Withdrawal";
      case "commission_payment":
        return "Commission Payment";
      default:
        return type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { class: "bg-green-100 text-green-800", text: "Completed" },
      pending: { class: "bg-yellow-100 text-yellow-800", text: "Pending" },
      failed: { class: "bg-red-100 text-red-800", text: "Failed" },
      cancelled: { class: "bg-gray-100 text-gray-800", text: "Cancelled" },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${config.class}`}
      >
        {config.text}
      </span>
    );
  };

  const getPaymentModeBadge = (mode) => {
    if (!mode) {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          N/A
        </span>
      );
    }

    const modeConfig = {
      cash: { class: "bg-orange-100 text-orange-800", text: "Cash" },
      online: { class: "bg-blue-100 text-blue-800", text: "Online" },
      upi: { class: "bg-purple-100 text-purple-800", text: "UPI" },
      bank_transfer: {
        class: "bg-indigo-100 text-indigo-800",
        text: "Bank Transfer",
      },
      cod: { class: "bg-orange-100 text-orange-800", text: "COD" },
    };

    const config = modeConfig[mode.toLowerCase()] || {
      class: "bg-gray-100 text-gray-800",
      text: mode.toUpperCase(),
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${config.class}`}
      >
        {config.text}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Expand transactions: add synthetic vendor payout entries for online bookings
  const expandedTransactions = useMemo(() => {
    if (!Array.isArray(transactions)) return [];

    const byBooking = new Map();
    transactions.forEach((item) => {
      let bid = item?.transaction?.bookingId;
      if (typeof bid === 'object' && bid !== null) {
        bid = bid._id || bid.bookingId;
      }
      bid = bid || item?.transaction?.referenceId;
      if (!bid) return;
      if (!byBooking.has(bid)) byBooking.set(bid, []);
      byBooking.get(bid).push(item);
    });

    const syntheticRows = [];
    byBooking.forEach((items, bid) => {
      const any = items[0];
      const isOnline = items.some(
        (i) => (i.transaction?.paymentMethod || '').toLowerCase() === 'online' ||
               i.transaction?.type?.includes('online')
      );
      if (!isOnline) return;

      // Determine ride amount (prefer explicit ride_payment_received or totalFare)
      const hasRidePayment = items.some(i => i.transaction?.type === 'ride_payment_received');
      const ridePayment = items.find(i => i.transaction?.type === 'ride_payment_received');
      const adminCredited = items.find(i => i.transaction?.type === 'admin_commission_credited');
      const fallbackWithFare = items.find(i => typeof i.transaction?.totalFare === 'number');
      const rideAmount = ridePayment?.transaction?.amount ?? fallbackWithFare?.transaction?.totalFare;
      const adminAmount = adminCredited?.transaction?.amount;

      // 1) Synthesize Ride Payment Received if missing
      if (!hasRidePayment && typeof rideAmount === 'number') {
        syntheticRows.push({
          driverName: any.driverName,
          driverPhone: any.driverPhone,
          driverVehicle: any.driverVehicle,
          driverEmail: any.driverEmail,
          transaction: {
            _id: `${bid}_synthetic_received`,
            type: 'ride_payment_received',
            amount: rideAmount,
            description: `Ride Payment Received - Booking #${any.transaction.bookingNumber || bid}`,
            date: any.transaction.date,
            createdAt: any.transaction.createdAt,
            status: 'completed',
            bookingId: bid,
            referenceId: bid,
            paymentMethod: 'online',
            bookingNumber: any.transaction.bookingNumber,
            totalFare: rideAmount
          }
        });
      }

      // 2) Synthesize Vendor Payout if we know ride + admin amounts
      if (typeof rideAmount === 'number' && typeof adminAmount === 'number') {
        const netPayout = Math.max(rideAmount - adminAmount, 0);
        syntheticRows.push({
          driverName: any.driverName,
          driverPhone: any.driverPhone,
          driverVehicle: any.driverVehicle,
          driverEmail: any.driverEmail,
          transaction: {
            _id: `${bid}_synthetic_payout`,
            type: 'commission_payout',
            amount: netPayout,
            description: `Net payout to vendor - Booking #${any.transaction.bookingNumber || bid}`,
            date: adminCredited?.transaction?.date || any.transaction.date,
            createdAt: adminCredited?.transaction?.createdAt || any.transaction.createdAt,
            status: 'completed',
            bookingId: bid,
            referenceId: bid,
            paymentMethod: 'online',
            bookingNumber: any.transaction.bookingNumber,
            totalFare: rideAmount
          }
        });
      }
    });

    return [...transactions, ...syntheticRows].sort((a, b) => new Date(b.transaction.date || b.transaction.createdAt) - new Date(a.transaction.date || a.transaction.createdAt));
  }, [transactions]);

  const filteredTransactions =
    filter === "all"
      ? expandedTransactions
      : expandedTransactions.filter((t) => t.transaction.type === filter);

  if (isLoading && (!transactions || transactions.length === 0)) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Recent Wallet Transactions</h2>
          <div className="animate-pulse h-8 w-24 bg-gray-200 rounded"></div>
        </div>
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className="animate-pulse flex items-center space-x-4"
            >
              <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
              <div className="h-4 bg-gray-200 rounded w-20"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <h2 className="text-xl font-semibold mb-4 md:mb-0">
          Recent Wallet Transactions
        </h2>

        <div className="flex flex-wrap gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="admin_commission_credited">Admin Commission Credited</option>
            <option value="admin_commission_deducted">Admin Commission Deducted</option>
            <option value="vendor_commission_credited">Vendor Commission Credited</option>
            <option value="ride_earning">Ride Earnings</option>
            <option value="commission_deduction">Commission</option>
            <option value="withdrawal_request">Withdrawals</option>
            <option value="commission_payment">Commission Payments</option>
          </select>

          <Link
            to="/admin/wallet/transactions"
            className="bg-primary text-white px-4 py-1 rounded-lg text-sm hover:bg-primary-dark transition-colors flex items-center"
          >
            <FaEye className="mr-2" />
            View All
          </Link>

          <button className="bg-gray-100 text-gray-700 px-4 py-1 rounded-lg text-sm hover:bg-gray-200 transition-colors flex items-center">
            <FaDownload className="mr-2" />
            Export
          </button>
        </div>
      </div>

      {filteredTransactions.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Driver
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Booking ID
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mode
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTransactions.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                        {item.driverName
                          ? item.driverName.charAt(0).toUpperCase()
                          : "D"}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {(() => {
                            let bid = item?.transaction?.bookingId;
                            if (typeof bid === 'object' && bid !== null) {
                              bid = bid._id || bid.bookingId;
                            }
                            bid = bid || item?.transaction?.referenceId || item?.transaction?.bookingNumber;
                            const fallback = bookingInfoMap[bid]?.driverName;
                            return item.driverName || fallback || "Unknown Driver";
                          })()}
                        </div>
                        <div className="text-sm text-gray-500">
                          {(() => {
                            let bid = item?.transaction?.bookingId;
                            if (typeof bid === 'object' && bid !== null) {
                              bid = bid._id || bid.bookingId;
                            }
                            bid = bid || item?.transaction?.referenceId || item?.transaction?.bookingNumber;
                            const fallback = bookingInfoMap[bid]?.driverVehicle;
                            return item.driverVehicle || fallback || "N/A";
                          })()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-gray-700">
                      {(() => {
                        const tx = item.transaction;
                        if (tx.bookingNumber) return String(tx.bookingNumber);
                        const bId = tx.bookingId;
                        if (typeof bId === 'object' && bId !== null) {
                          return bId.bookingId || bId.bookingNumber || bId.customBookingId || bId._id || '—';
                        }
                        return bId || tx.referenceId || '—';
                      })()}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center">
                      <div className="mr-2">
                        {getTransactionIcon(item.transaction.type)}
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {getTransactionTypeDisplay(item.transaction.type)}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span
                      className={`text-sm font-semibold ${
                        item.transaction.type === "ride_earning" ||
                        item.transaction.type === "online_ride_earning" ||
                        item.transaction.type === "earning" ||
                        item.transaction.type === "commission_payment" ||
                        item.transaction.type === "deposit" ||
                        item.transaction.type === "wallet_topup" ||
                        item.transaction.type === "ride_payment_received" ||
                        item.transaction.type === "admin_commission_credited"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {item.transaction.type === "commission_deduction" ||
                       item.transaction.type === "withdrawal_request" ||
                       item.transaction.type === "cod_commission_deduction" ||
                       item.transaction.type === "commission_payout" ||
                       item.transaction.type === "vendor_commission_credited" ||
                       item.transaction.type === "admin_commission_deducted"
                        ? "-"
                        : "+"}
                      {formatCurrency(item.transaction.amount)}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    {getStatusBadge(item.transaction.status)}
                  </td>
                  <td className="py-4 px-4">
                    {getPaymentModeBadge(
                      item.transaction.paymentMode ||
                      item.transaction.paymentMethod ||
                      (item.transaction.type?.includes('online') ? 'online' :
                       item.transaction.type?.includes('cod') ? 'cash' : 'online')
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-gray-500">
                      {formatDate(item.transaction.date)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {hasMore && (
            <div className="mt-6 text-center">
              <button
                onClick={onLoadMore}
                disabled={isLoading}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {isLoading ? "Loading..." : "Load More Transactions"}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <FaRupeeSign className="text-gray-400 text-2xl" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Transactions Found
          </h3>
          <p className="text-gray-500">
            {filter === "all"
              ? "No wallet transactions have been recorded yet."
              : `No ${getTransactionTypeDisplay(filter).toLowerCase()} transactions found.`}
          </p>
        </div>
      )}
    </div>
  );
};

export default RecentTransactionsTable;
